import React, { Suspense, lazy, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Gavel } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import ProductCard from "./ProductCard";
import api, { money } from "../lib/api";
import { shouldReduceFx } from "./Dragon3D";
import ErrorBoundary from "./ErrorBoundary";

const Dragon3D = lazy(() => import("./Dragon3D"));
const OrbitalDragonCard = lazy(() => import("./OrbitalDragonCard"));

const fadeUp = {
  initial: { opacity: 0, y: 24, filter: "blur(6px)" },
  whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

function HeroDragon({ params, dragons }) {
  const cfg = dragons?.find((d) => d.section === (params.dragon_section || "home_hero"));
  const [reduceFx, setReduceFx] = useState(true);
  useEffect(() => {
    setReduceFx(!cfg?.enabled || shouldReduceFx(cfg));
  }, [cfg]);

  return (
    <section className="relative min-h-[82vh] flex items-center overflow-hidden" data-testid="block-hero-dragon">
      {params.bg_image && (
        <div className="absolute inset-0">
          <img src={params.bg_image} alt="" className="w-full h-full object-cover opacity-40" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
        </div>
      )}
      {!reduceFx && cfg && (
        <div className="absolute right-0 top-0 bottom-0 w-full lg:w-[55%] opacity-90">
          <ErrorBoundary>
            <Suspense fallback={null}>
              <Dragon3D cfg={cfg} className="w-full h-full" />
            </Suspense>
          </ErrorBoundary>
        </div>
      )}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 w-full">
        <motion.div {...fadeUp} className="max-w-2xl">
          <p className="label-caps mb-6">{params.tagline || "Coleção oriental autêntica"}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-white leading-[1.05]" data-testid="hero-title">
            {params.title}
          </h1>
          <p className="mt-6 text-base md:text-lg text-zinc-300 leading-relaxed max-w-lg" data-testid="hero-subtitle">
            {params.subtitle}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Button asChild size="lg" data-testid="hero-cta"
              className="bg-gold text-black hover:bg-gold-light font-semibold px-8 transition-colors">
              <Link to={params.cta_link || "/"}>{params.cta_label || "Explorar"} <ArrowRight size={16} className="ml-2" /></Link>
            </Button>
            <Button asChild variant="outline" size="lg" data-testid="hero-secondary-cta"
              className="border-gold/40 text-gold hover:bg-amber-500/10 px-8 transition-colors">
              <Link to="/autenticidade"><ShieldCheck size={16} className="mr-2" /> Verificar autenticidade</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function CategoryStrip({ params, categories }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24" data-testid="block-category-strip">
      <motion.p {...fadeUp} className="label-caps mb-4">Explore</motion.p>
      <motion.h2 {...fadeUp} className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-12">{params.title || "Categorias"}</motion.h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        {(categories || []).map((c, i) => (
          <motion.div key={c.id} {...fadeUp} transition={{ duration: 0.6, delay: i * 0.08 }}>
            <Link to={`/categoria/${c.slug}`} data-testid={`category-tile-${c.slug}`}
              className="group relative block aspect-[4/5] rounded-lg overflow-hidden border border-white/10 hover:border-gold/50 transition-colors">
              {c.image && <img src={c.image} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
              <div className="absolute bottom-0 p-5">
                <p className="font-display text-xl text-white group-hover:text-gold transition-colors">{c.name}</p>
                <p className="text-xs text-zinc-400 mt-1 line-clamp-2">{c.description}</p>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function ProductGrid({ params, dragons, settings }) {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    const query = params.filter === "featured" ? "?featured=true" : params.filter === "digital" ? "?type=digital" : "";
    api.get(`/products${query}`).then((r) => setProducts((r.data || []).slice(0, params.limit || 8))).catch(() => {});
  }, [params.filter, params.limit]);

  const dragonCfg = dragons?.find((d) => d.section === "product_cards" && d.enabled);

  if (!products.length) return null;

  const globalColor = settings?.dragon_color_default || "#D4AF37";
  const globalEmissive = settings?.dragon_emissive_default ?? 0.35;
  const dragonColor = params.dragon_color || (dragonCfg?.use_global_color ? globalColor : dragonCfg?.color) || globalColor;
  const auraColor = dragonColor;
  const resolvedCfg = dragonCfg && {
    ...dragonCfg,
    color: dragonColor,
    emissive: dragonCfg.use_global_color ? globalEmissive : dragonCfg.emissive,
  };

  return (
    <section className="relative max-w-7xl mx-auto px-6 lg:px-12 py-16" data-testid={`block-product-grid-${params.filter || "all"}`}>
      {params.flame_effect === "on" && <div className="flame-bg" data-testid="flame-effect" aria-hidden />}
      <motion.h2 {...fadeUp} className="relative z-10 font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-12">{params.title || "Produtos"}</motion.h2>
      <div className={products.length === 1 ? "relative z-10 flex justify-center pb-14" : "relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 pb-14"}>
        {products.map((p, i) => (
          <motion.div key={p.id} {...fadeUp} transition={{ duration: 0.6, delay: i * 0.06 }} className={products.length === 1 ? "w-full max-w-sm" : undefined}>
            {i === 0 && resolvedCfg ? (
              <ErrorBoundary fallback={<ProductCard product={p} auraColor={auraColor} />}>
                <Suspense fallback={<ProductCard product={p} auraColor={auraColor} />}>
                  <OrbitalDragonCard cfg={resolvedCfg}>
                    <ProductCard product={p} auraColor={auraColor} />
                  </OrbitalDragonCard>
                </Suspense>
              </ErrorBoundary>
            ) : (
              <ProductCard product={p} auraColor={auraColor} />
            )}
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function BannerBlock({ params, banners }) {
  const banner = (banners || [])[params.banner_index || 0];
  if (!banner) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-12" data-testid="block-banner">
      <motion.div {...fadeUp}>
        <Link to={banner.link || "/"} className="group relative block rounded-lg overflow-hidden border border-white/10 hover:border-gold/40 transition-colors min-h-[280px] flex items-center">
          {banner.image && <img src={banner.image} alt={banner.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />}
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-transparent" />
          <div className="relative p-10 lg:p-16 max-w-xl">
            <p className="font-display text-2xl sm:text-3xl text-white group-hover:text-gold transition-colors">{banner.title}</p>
            <p className="text-sm text-zinc-300 mt-3">{banner.subtitle}</p>
            <span className="inline-flex items-center gap-2 mt-6 text-sm text-gold">Ver coleção <ArrowRight size={15} /></span>
          </div>
        </Link>
      </motion.div>
    </section>
  );
}

function AuthenticityCTA({ params }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24" data-testid="block-authenticity-cta">
      <motion.div {...fadeUp} className="border border-gold/25 rounded-lg bg-gradient-to-br from-[#141414] to-black p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center gap-10 gold-glow">
        <ShieldCheck size={48} className="text-gold shrink-0" />
        <div className="flex-1">
          <h2 className="font-display text-2xl sm:text-3xl tracking-tight font-medium">{params.title}</h2>
          <p className="text-zinc-400 mt-3 max-w-xl leading-relaxed">{params.text}</p>
        </div>
        <Button asChild data-testid="authenticity-cta-button"
          className="bg-gold text-black hover:bg-gold-light font-semibold px-8 transition-colors shrink-0">
          <Link to="/autenticidade">Verificar agora</Link>
        </Button>
      </motion.div>
    </section>
  );
}

function AuctionCountdown({ end }) {
  const [txt, setTxt] = useState("");
  useEffect(() => {
    const tick = () => {
      const ms = new Date(end).getTime() - Date.now();
      if (ms <= 0) return setTxt("encerrando...");
      const s = Math.floor(ms / 1000);
      const d = Math.floor(s / 86400);
      const h = Math.floor((s % 86400) / 3600);
      const m = Math.floor((s % 3600) / 60);
      setTxt(d > 0 ? `${d}d ${h}h ${m}min` : `${h}h ${String(m).padStart(2, "0")}min`);
    };
    tick();
    const i = setInterval(tick, 30000);
    return () => clearInterval(i);
  }, [end]);
  return <>{txt}</>;
}

function AuctionStrip({ params }) {
  const [auctions, setAuctions] = useState([]);
  useEffect(() => {
    api.get("/auctions/active").then((r) => setAuctions(r.data || [])).catch(() => {});
  }, []);
  if (!auctions.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24" data-testid="block-auction-strip">
      <motion.h2 {...fadeUp} className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium mb-12 flex items-center gap-3">
        <Gavel size={26} className="text-gold" /> {params.title || "Leilões ativos"}
      </motion.h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {auctions.map((a, i) => (
          <motion.div key={a.id} {...fadeUp} transition={{ duration: 0.6, delay: i * 0.06 }}>
            <Link to={`/produto/${a.product?.slug}`} data-testid={`auction-card-${a.id}`}
              className="group block border border-gold/25 rounded-lg overflow-hidden bg-[#111111] hover:border-gold/60 transition-colors gold-glow">
              {a.product?.image && <img src={a.product.image} alt={a.product?.name} loading="lazy" className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-700" />}
              <div className="p-5">
                <p className="font-display text-lg text-white group-hover:text-gold transition-colors">{a.product?.name}</p>
                <p className="text-sm text-gold mt-2" data-testid={`auction-bid-${a.id}`}>
                  {a.current_bid ? `Lance atual: ${money(a.current_bid.amount)}` : `Lance inicial: ${money(a.starting_price)}`}
                </p>
                <p className="text-xs text-zinc-500 mt-1">Termina em <AuctionCountdown end={a.end_at} /></p>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

const BLOCKS = {
  hero_dragon: HeroDragon,
  category_strip: CategoryStrip,
  product_grid: ProductGrid,
  banner: BannerBlock,
  authenticity_cta: AuthenticityCTA,
  auction_strip: AuctionStrip,
};

export default function BlockRenderer({ blocks, categories, banners, dragons, settings }) {
  return (
    <>
      {(blocks || []).map((block) => {
        const Component = BLOCKS[block.type];
        if (!Component) return null;
        return (
          <Component
            key={block.id}
            params={block.params || {}}
            categories={categories}
            banners={banners}
            dragons={dragons}
            settings={settings}
          />
        );
      })}
    </>
  );
}
